import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

/** Slide-out duration; keep in step with the `toast-out` keyframes. */
const LEAVE_MS = 180;

/**
 * Transient floating confirmation. Feed it a message signal and clear that
 * signal on `dismissed` so the same text can be shown again later.
 */
@Component({
  selector: 'app-toast',
  standalone: true,
  templateUrl: './toast.component.html',
  styleUrl: './toast.component.scss',
})
export class ToastComponent {
  readonly message = input<string | null>(null);
  readonly tone = input<'success' | 'error'>('success');
  /** Milliseconds on screen before auto-dismiss; 0 keeps it until dismissed. */
  readonly duration = input(4000);
  readonly dismissed = output<void>();

  private readonly shown = signal<string | null>(null);
  private readonly leaving = signal(false);
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private removeTimer: ReturnType<typeof setTimeout> | null = null;

  readonly text = computed(() => this.shown());
  readonly isLeaving = computed(() => this.leaving());

  constructor() {
    effect(() => {
      const message = this.message();
      this.clearTimers();
      this.leaving.set(false);
      this.shown.set(message);
      const duration = this.duration();
      if (message && duration > 0) {
        this.hideTimer = setTimeout(() => this.dismiss(), duration);
      }
    });
    inject(DestroyRef).onDestroy(() => this.clearTimers());
  }

  dismiss(): void {
    if (!this.shown() || this.leaving()) return;
    this.leaving.set(true);
    this.removeTimer = setTimeout(() => {
      this.shown.set(null);
      this.leaving.set(false);
      this.dismissed.emit();
    }, LEAVE_MS);
  }

  private clearTimers(): void {
    if (this.hideTimer) clearTimeout(this.hideTimer);
    if (this.removeTimer) clearTimeout(this.removeTimer);
    this.hideTimer = null;
    this.removeTimer = null;
  }
}
